import { useEffect, useState } from 'react'

/**
 * "We're doing maintenance": driven by an object in R2, not by the api.
 *
 * WHY NOT THE API
 * ---------------
 * The banner has to be visible when the api is down, because the api going
 * down IS the maintenance. Anything served only by the api is unreachable at
 * exactly the moment it matters.
 *
 * `/maintenance.json` is `site/maintenance.json` in the site's R2 bucket. On
 * prod the Cloudflare Worker answers it straight from R2, with no origin, so it
 * works with the api down. It used to be a file in web/static, which the sealed
 * prod container does not mount (audit-20260915 HIGH-3). Present means
 * maintenance; ABSENT (404) means normal, so the default state needs no object
 * and cannot be left switched on by a failed write. Turning it on and off is
 * `fth maint on|off`: no deploy, no rebuild, no restart.
 *
 * WHY IT POLLS
 * ------------
 * A visitor who loaded the page five minutes ago is exactly who you want to
 * warn before the box goes down. Checking only on mount would tell nobody who
 * is already here — which is everyone who matters during a planned window.
 *
 * Cache-busted per request: the file is served by the same static handler as
 * hashed assets, and a cached 404 would keep the banner hidden through the
 * whole window.
 */

const POLL_MS = 45_000

interface Maint {
  active?: boolean
  message?: string
  eta?: string
}

export default function MaintenanceBanner() {
  const [m, setM] = useState<Maint | null>(null)

  useEffect(() => {
    let cancelled = false

    const check = async () => {
      try {
        const r = await fetch(`/maintenance.json?t=${Date.now()}`, { cache: 'no-store' })
        if (!r.ok) {
          if (!cancelled) setM(null) // 404 = not in maintenance. The normal case.
          return
        }
        const j = await r.json()
        if (!cancelled) setM(j?.active ? j : null)
      } catch {
        // Network error is NOT maintenance. Showing the banner because a fetch
        // failed would put it up during any transient blip, and a banner that
        // cries wolf gets ignored on the day it is true.
        if (!cancelled) setM(null)
      }
    }

    check()
    const id = setInterval(check, POLL_MS)
    return () => { cancelled = true; clearInterval(id) }
  }, [])

  if (!m) return null

  return (
    <div
      role="status"
      className="w-full bg-amber-500 text-[#1a1a1a] px-4 py-2 text-sm font-medium text-center"
      style={{ position: 'sticky', top: 0, zIndex: 200 }}
    >
      {m.message || 'Scheduled maintenance — the site may be briefly unavailable.'}
      {m.eta ? <span className="font-semibold"> Back around {m.eta}.</span> : null}
    </div>
  )
}
